/**
 * Templates PDF: folha A4 (referência/legado) e unidade (1 etiqueta).
 *
 * A geração V1 carimba a unidade nos slots ocupados de uma folha em branco
 * e desenha texto/QR por cima — sem reutilizar a grade de 10 molduras vazias.
 * Atualizar a arte: substituir o SEM-TEXTO em assets/pdf/ e regenerar as
 * unidades com `scripts/extract-unit-label-pdfs.py` (mesmo LABEL_BOX).
 */
import templateDourado from "./assets/pdf/PDF_0-9_DOURADO-SEM-TEXTO.pdf";
import templatePreto from "./assets/pdf/PDF_0-9_PRETO-SEM-TEXTO.pdf";
import templateColorida from "./assets/pdf/PDF_0-9_COLORIDA-SEM-TEXTO.pdf";
import templateColoridaVazio from "./assets/pdf/PDF_VAZIO_COLORIDA-SEM-TEXTO.pdf";
import templateVazioPadronizado from "./assets/pdf/VAZIO-PADRONIZADO.pdf";

import unitMajorca from "./assets/pdf/unit/unit-majorca.pdf";
import unitDouradoPreto from "./assets/pdf/unit/unit-dourado-preto.pdf";
import unitColorida from "./assets/pdf/unit/unit-colorida.pdf";
import unitColoridaVazio from "./assets/pdf/unit/unit-colorida-vazio.pdf";
import unitVazio from "./assets/pdf/unit/unit-vazio.pdf";

/** Arte por template visual (unidade = 1 etiqueta, sem grade). */
const TEMPLATE_BUCKETS = {
  majorca: {
    label: "Majorca",
    paper: "majorca",
    url: templateDourado,
    unitUrl: unitMajorca,
  },
  dourado: {
    label: "Dourado (preto)",
    paper: "dourado",
    url: templatePreto,
    unitUrl: unitDouradoPreto,
  },
  dourado_quad: {
    label: "Dourado Quad 10",
    paper: "dourado",
    url: templateDourado,
    unitUrl: unitMajorca,
  },
  colorida: {
    label: "Colorida",
    paper: "colorido",
    url: templateColorida,
    unitUrl: unitColorida,
  },
  colorida_vazio: {
    label: "Colorida vazia",
    paper: "colorido",
    url: templateColoridaVazio,
    unitUrl: unitColoridaVazio,
  },
  vazio: {
    label: "Vazio padronizado",
    paper: "majorca",
    url: templateVazioPadronizado,
    unitUrl: unitVazio,
  },
  art: {
    label: "Arte estendida",
    paper: "art",
    url: null,
    unitUrl: null,
  },
};

/** ZIP achatado V1 — um PDF multipage por papel de estoque. */
const PAPER_ORDER = ["dourado", "majorca", "colorido", "art"];

const PAPER_ZIP = {
  dourado: { label: "Dourado", zipFile: "dourado.pdf" },
  majorca: { label: "Majorca", zipFile: "majorca.pdf" },
  colorido: { label: "Colorido", zipFile: "colorido.pdf" },
  art: { label: "Arte estendida", zipFile: "art.pdf" },
};

const BUCKET_BY_VISUAL = {
  majorca_legado: "majorca",
  dourado_black: "dourado",
  dourado_quad: "dourado_quad",
  colorido_legado: "colorida",
  colorido_vazio: "colorida_vazio",
  majorca_authentic: "vazio",
  majorca_authentic_auto: "vazio",
  majorca_no_subgrades: "vazio",
  majorca_no_subgrades_auto: "vazio",
  art_extended: "art",
};

const bucketForVisual = (visual) => BUCKET_BY_VISUAL[visual] || "majorca";

const templateBucket = (bucket) => TEMPLATE_BUCKETS[bucket] || TEMPLATE_BUCKETS.majorca;

const paperMeta = (paper) => PAPER_ZIP[paper] || PAPER_ZIP.majorca;

const unitBytesCache = new Map();

/** Bytes do PDF unitário (1 etiqueta), cache por bucket. */
const fetchUnitTemplateBytes = async (bucket) => {
  const key = TEMPLATE_BUCKETS[bucket] ? bucket : "majorca";
  if (key === "art") throw new Error("Papel art não usa template de unidade");
  if (unitBytesCache.has(key)) return unitBytesCache.get(key);
  const promise = fetch(templateBucket(key).unitUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`Unidade ${key} indisponível (${res.status})`);
      return res.arrayBuffer();
    })
    .catch((err) => {
      unitBytesCache.delete(key);
      throw err;
    });
  unitBytesCache.set(key, promise);
  return promise;
};

/** @deprecated Prefer fetchUnitTemplateBytes — mantido para testes de referência. */
const bytesCache = new Map();
const fetchTemplateBytes = async (bucket) => {
  const key = TEMPLATE_BUCKETS[bucket] ? bucket : "majorca";
  if (bytesCache.has(key)) return bytesCache.get(key);
  const promise = fetch(templateBucket(key).url)
    .then((res) => {
      if (!res.ok) throw new Error(`Template ${key} indisponível (${res.status})`);
      return res.arrayBuffer();
    })
    .catch((err) => {
      bytesCache.delete(key);
      throw err;
    });
  bytesCache.set(key, promise);
  return promise;
};

export {
  TEMPLATE_BUCKETS,
  PAPER_ORDER,
  PAPER_ZIP,
  BUCKET_BY_VISUAL,
  bucketForVisual,
  templateBucket,
  paperMeta,
  fetchUnitTemplateBytes,
  fetchTemplateBytes,
};
